import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { clearAdminPassword, hasAdminPassword, setAdminPassword } from '@/lib/admin-auth';
import { formatBytes, formatDate } from '@/lib/utils';

export const AdminPage = () => {
  const queryClient = useQueryClient();
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(hasAdminPassword());

  const { data, isLoading, error } = useQuery({
    queryKey: ['stats'],
    queryFn: api.getStats,
    retry: false,
    enabled: isUnlocked,
  });

  const unlockMutation = useMutation({
    mutationFn: async () => {
      const value = passwordInput.trim();
      if (!value) {
        throw new Error('Password admin wajib diisi.');
      }

      setAdminPassword(value);
      return api.getStats();
    },
    onSuccess: () => {
      setAuthError('');
      setIsUnlocked(true);
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
    onError: (err) => {
      clearAdminPassword();
      setIsUnlocked(false);
      setAuthError(err instanceof Error ? err.message : 'Password admin salah.');
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Pilih gambar dulu.');
      return api.uploadPost(file, caption);
    },
    onSuccess: async () => {
      setCaption('');
      setFile(null);
      await queryClient.invalidateQueries({ queryKey: ['stats'] });
      await queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (postId: string) => api.deletePost(postId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['stats'] });
      await queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
  });

  if (!isUnlocked) {
    return (
      <section className="space-y-6">
        <div className="space-y-3">
          <p className="text-sm uppercase tracking-[0.25em] text-white/45">Protected admin workspace</p>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Unlock admin dashboard
          </h1>
          <p className="max-w-2xl text-sm text-white/65">
            Masukkan password admin sekali saja. Setelah masuk, kamu tidak akan diminta lagi selama tab ini masih terbuka.
          </p>
        </div>

        <div className="glass rounded-[28px] p-5">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              unlockMutation.mutate();
            }}
          >
            <div className="space-y-2">
              <label className="text-sm text-white/70">Admin password</label>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Masukkan password admin"
                className="block w-full rounded-2xl border border-white/10 bg-white/5 p-3 text-sm text-white placeholder:text-white/35 focus:border-white/20 focus:outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={unlockMutation.isPending}
              className="rounded-full bg-white px-5 py-3 text-sm font-medium text-black disabled:opacity-60"
            >
              {unlockMutation.isPending ? 'Checking…' : 'Unlock admin'}
            </button>

            {authError && (
              <div className="rounded-2xl border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-200">
                {authError}
              </div>
            )}
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-6">
      <div className="space-y-3">
        <p className="text-sm uppercase tracking-[0.25em] text-white/45">Protected admin workspace</p>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Manage uploads, captions, comments, and gallery health.
          </h1>
          <button
            type="button"
            onClick={() => {
              clearAdminPassword();
              setPasswordInput('');
              setAuthError('');
              setIsUnlocked(false);
              queryClient.removeQueries({ queryKey: ['stats'] });
            }}
            className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80 hover:bg-white/10"
          >
            Lock admin
          </button>
        </div>
      </div>

      <div className="glass rounded-[28px] p-5">
        <h2 className="text-lg font-semibold">Upload photo</h2>
        <p className="mt-1 text-sm text-white/55">Pilih gambar lalu tambahkan caption singkat.</p>

        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            uploadMutation.mutate();
          }}
        >
          <div className="space-y-2">
            <label className="text-sm text-white/70">Image file</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full rounded-2xl border border-white/10 bg-white/5 p-3 text-sm text-white file:mr-3 file:rounded-full file:border-0 file:bg-white file:px-4 file:py-2 file:text-sm file:font-medium file:text-black"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm text-white/70">Caption</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Tulis caption foto..."
              rows={4}
              className="block w-full rounded-2xl border border-white/10 bg-white/5 p-3 text-sm text-white placeholder:text-white/35 focus:border-white/20 focus:outline-none"
            />
          </div>

          <button
            type="submit"
            disabled={uploadMutation.isPending}
            className="rounded-full bg-white px-5 py-3 text-sm font-medium text-black disabled:opacity-60"
          >
            {uploadMutation.isPending ? 'Uploading…' : 'Upload photo'}
          </button>

          {uploadMutation.isError && (
            <div className="rounded-2xl border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-200">
              {uploadMutation.error instanceof Error ? uploadMutation.error.message : 'Upload gagal.'}
            </div>
          )}

          {uploadMutation.isSuccess && (
            <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-3 text-sm text-emerald-200">
              Upload berhasil.
            </div>
          )}
        </form>
      </div>

      {isLoading && <div className="glass rounded-[28px] p-6 text-sm text-white/70">Loading dashboard…</div>}
      {error && (
        <div className="rounded-[28px] border border-red-500/20 bg-red-500/10 p-6 text-sm text-red-200">
          Authentication failed or API unavailable.
        </div>
      )}

      {data?.stats && (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {[
              ['Total Photos', data.stats.totalPhotos],
              ['Total Comments', data.stats.totalComments],
              ['Total Reactions', data.stats.totalReactions],
              ['Storage Usage', formatBytes(data.stats.storageUsageBytes)],
            ].map(([label, value]) => (
              <div key={label} className="glass rounded-[28px] p-5">
                <p className="text-sm text-white/55">{label}</p>
                <p className="mt-3 text-3xl font-semibold tracking-tight">{value}</p>
              </div>
            ))}
          </div>

          <div className="glass rounded-[28px] p-5">
            <h2 className="text-lg font-semibold">Latest uploads</h2>
            <div className="mt-4 space-y-3">
              {data.stats.latestUploads.length === 0 && (
                <p className="text-sm text-white/55">Belum ada upload.</p>
              )}

              {data.stats.latestUploads.map((upload) => (
                <div
                  key={upload.id}
                  className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-3"
                >
                  <img
                    src={upload.imageUrl}
                    alt={upload.caption}
                    className="h-16 w-16 rounded-2xl object-cover"
                    loading="lazy"
                  />
                  <div className="flex flex-1 items-center justify-between">
                    <div>
                      <p className="text-sm text-white">{upload.caption}</p>
                      <p className="text-xs text-white/45">{formatDate(upload.createdAt)}</p>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        if (!window.confirm('Delete this photo?\n\nThis action cannot be undone.')) return;
                        deleteMutation.mutate(upload.id);
                      }}
                      disabled={deleteMutation.isPending}
                      className="rounded-full border border-red-500/20 bg-red-500/10 px-4 py-2 text-xs font-medium text-red-300 hover:bg-red-500/20 disabled:opacity-50"
                    >
                      {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
};